import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiVideoGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-video-generator',
			name: 'AI Video Generator — Create Videos with AI by Lift',
			description:
				'Generate videos with AI from any prompt or image. Choose a video model, set the ratio, duration, and resolution, and download a clip ready to share. Try Lift today!',
			url: 'https://liftapp.ai/ai-video-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-video-generator#product' },
				{ '@id': 'https://liftapp.ai/ai-video-generator#howto' },
				{ '@id': 'https://liftapp.ai/ai-video-generator#benefits' },
				{ '@id': 'https://liftapp.ai/ai-video-generator#faq' },
				{ '@id': 'https://liftapp.ai/ai-video-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-video-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-video-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-video-generator#product',
			name: 'AI Video Generator',
			description:
				'Generate videos with AI from any prompt or image. Choose a video model, set the ratio, duration, and resolution, and download a clip ready to share.',
			url: 'https://liftapp.ai/ai-video-generator',
			fileFormat: ['video/mp4'],
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-video-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Describe Your Video',
					text: 'Type what you want to see in any language — or tap Improve Prompt to turn your idea into a richer, more detailed description.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Pick A Model And Settings',
					text: 'Choose an AI video model, then set the aspect ratio, resolution, duration, and audio — the available options adapt to the model you pick.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Add Frames Or A Reference',
					text: 'Optionally upload a start frame, an end frame, or a reference image to control exactly how your clip begins, ends, and looks.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download And Use',
					text: 'Download your video or publish it straight to your social feeds.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-video-generator#benefits',
			name: 'Who can benefit from AI Video Generator?',
			description:
				"Lift's AI Video Generator helps content creators, marketers, e-commerce sellers, educators, and casual creators turn ideas into ready-to-share video clips.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Make Reels, TikToks, and Shorts from a single prompt — no filming, no editing timeline. Regenerate any clip to get a fresh take with the same settings.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Marketers & SMM managers — Produce video ads and promo clips without a production crew. Test different hooks and formats by simply changing the prompt or the model.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'E-commerce sellers — Turn product photos into moving showcases that stop the scroll — start from an image and let AI handle the motion.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Educators & explainers — Bring concepts, scenes, and stories to the screen when filming them would be impossible or costly.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Casual creators — Greetings, memes, and just-for-fun clips from whatever idea comes to mind — every AI generated video lands in your gallery, ready to share.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI Video Generator Examples by Lift',
				description:
					'Examples of AI generated videos created with the Lift AI video maker.',
			},
		},
		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-video-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Video Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Video Generator is a premium online tool that turns your prompt or image into a finished video clip. It brings together leading AI video models — including Seedance, Veo, Kling, and Hailuo — so you can pick the best fit for every project.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Video Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Describe your video — or let Improve Prompt polish the idea for you — then choose a model and adjust the ratio, resolution, and duration. Optionally add a start frame, end frame, or reference image. Hit Generate and receive your AI-created clip in moments — no filming or editing experience needed.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I generate AI videos with sound?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Turn on the audio option and models that support sound will render it together with the visuals, so your clip arrives ready to post without a separate audio edit.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All videos generated in Lift can be exported without watermark when you're on the Pro plan. Download them in high resolution — ready for social media, ads, or presentations.",
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my photos to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your photos securely and uses them only to generate your results.',
					},
				},
			],
		},
		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-video-generator#why-lift',
			name: 'Why Lift AI Video Generator stands out',
			description:
				"Lift's AI Video Generator combines professional-grade results, intelligent rendering, cross-platform access, and affordability.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Best AI Video Generator by Lift',
				description: 'Best AI video generator — Lift.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-video-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			itemListElement: [...exploreSchemaItemList.itemListElement],
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-video-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Video Generator',
					item: 'https://liftapp.ai/ai-video-generator',
				},
			],
		},
	],
};
